// library imports
import { Switch, TimePicker } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState, useContext } from "react";
import { useForm, useWatch } from "antd/es/form/Form";
// component imports
import EditableTable from "../common/EditableTable";
// hool imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";

const ISOWEEKDAY = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export default function WeekdaySchedule() {
  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // load index context
  const { messageApi, siteSelected } = useContext(IndexContext);

  // form state
  const [form] = useForm();
  const [scheduleData, setScheduleData] = useState([]);
  const is_business_day = useWatch("BusinessDay", form);

  /** Convert API data to fit schedule */
  const convertData = (scheduleItem) => {
    let start_time =
      scheduleItem?.StartTime != null
        ? dayjs()
            .hour(scheduleItem.StartTime.split(":")[0])
            .minute(scheduleItem.StartTime.split(":")[1])
        : null;
    let end_time =
      scheduleItem?.EndTime != null
        ? dayjs()
            .hour(scheduleItem.EndTime.split(":")[0])
            .minute(scheduleItem.EndTime.split(":")[1])
        : null;
    return {
      ID: scheduleItem.ID,
      Weekday: ISOWEEKDAY[scheduleItem?.IsoWeekDay - 1],
      BusinessDay: scheduleItem?.IsWorkingDay === 1,
      BusinessHours: [start_time, end_time],
    };
  };

  /** Convert table data to fit schedule */
  const reverseConversion = (scheduleItem) => {
    let optional_item = {};
    if (scheduleItem?.Weekday) {
      optional_item.IsoWeekDay = ISOWEEKDAY.findIndex(
        (weekday) => weekday === scheduleItem.Weekday
      );
    }
    return {
      ...optional_item,
      IsWorkingDay: scheduleItem.BusinessDay ? 1 : 0,
      StartTime:
        scheduleItem.BusinessHours[0] != null
          ? scheduleItem.BusinessHours[0].format("HH:mm")
          : null,
      EndTime:
        scheduleItem.BusinessHours[1] != null
          ? scheduleItem.BusinessHours[1].format("HH:mm")
          : null,
    };
  };

  const convertedData = scheduleData.map(convertData);

  const columns = [
    {
      title: "",
      dataIndex: "Weekday",
      key: "Weekday",
    },
    {
      title: "Operation Day",
      dataIndex: "BusinessDay",
      key: "BusinessDay",
      editable: true,
      fieldComponent: (
        <Switch
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
        />
      ),
      render: (val, record, index) =>
        val ? (
          <CheckCircleFilled style={{ color: "green" }} />
        ) : (
          <CloseCircleFilled style={{ color: "red" }} />
        ),
    },
    {
      title: "Operation Hours",
      dataIndex: "BusinessHours",
      key: "BusinessHours",
      editable: true,
      fieldComponent: <TimePicker.RangePicker format={"HH:mm"} />,
      formItemProps: {
        name: "BusinessHours",
        style: {
          margin: 0,
        },
        hidden: is_business_day ? false : true,
      },
      render: (val, record, index) => {
        if (val?.length && val[0] && val[1] && record?.BusinessDay === true) {
          return val[0].format("HH:mm") + " - " + val[1].format("HH:mm");
        } else {
          return "";
        }
      },
    },
  ];

  /** API to get weekly schedule */
  function getWeeklyScheduleData() {
    fetchWrapper({
      endpoint_url: "ai_bess/getAllWeeklySchedule",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },
      onSuccess: (response) => {
        setScheduleData(response.data);
      },
    });
  }

  /** API to update weekly schedule */
  function updateWeeklySchedule(send_data) {
    fetchWrapper({
      endpoint_url: "ai_bess/updateWeeklySchedule",
      method: "POST",
      body: {
        updated_data: send_data,
      },
      onSuccess: (response) => {
        messageApi.open({
          type: "success",
          content: response.message,
        });
        getWeeklyScheduleData();
      },
    });
  }

  useEffect(() => {
    getWeeklyScheduleData();
  }, []);

  return (
    <>
      <EditableTable
        form={form}
        pagination={{ pageSize: 7 }}
        columns={columns}
        dataSource={convertedData}
        onSave={(key, val) => {
          let new_data = [...scheduleData];
          let old_schedule = new_data[key];
          new_data[key] = { ...old_schedule, ...reverseConversion(val) };
          setScheduleData(new_data);
          updateWeeklySchedule(new_data[key]);
        }}
      />
    </>
  );
}
